import React, {useEffect, useState} from 'react';
import SubscriptionSettingsUI from '../view/SubscriptionSettingsUI';
import {inject, observer} from 'mobx-react';
import {useNavigate, useParams} from 'react-router-dom';
import SubscriptionSettingsStore from '../store/SubscriptionSettingsStore';
import {OrganizationSubscription} from '../../../../data/models/Subscription/Subscription';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import {settingsText} from '../../settingsText';
import {InvoiceListItem} from '../../../../data/models/Subscription/Invoice';
import FeaturesTableModal from '../components/FeaturesTableModal';
import {
  isAnnualSubscription,
  SUBSCRIPTION_ENTERPRISE,
  SUBSCRIPTION_TRIAL,
} from '../../../Subscription/config';
import ModalProviderStore from '../../../../modules/ModalProvider/store/ModalProviderStore';
import {CONFIRM_PAYMENT_MODAL} from '../../../../modules/ModalProvider/globalModals';
import {ConfirmPaymentPayload} from '../../../../uiToolkit/ConfirmPaymentModal/ConfirmPaymentModal';
import {getSubscriptionPlan} from '../config';
import {formatDateTimeTextFormat} from '../../../../utils/notificationTimeTextConvertor';

interface Props {
  subscriptionSettingsStore?: SubscriptionSettingsStore;
  modalProviderStore?: ModalProviderStore;
}

const SubscriptionSettingsContainer = inject(
  'subscriptionSettingsStore',
  'modalProviderStore',
)(
  observer(({subscriptionSettingsStore, modalProviderStore}: Props) => {
    const [subscription, setSubscription] =
      useState<OrganizationSubscription>();
    const {organizationId} = useParams();
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
    const [showFeatures, setShowFeatures] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      if (organizationId) {
        subscriptionSettingsStore!
          .getOrganizationSubscription(organizationId)
          .then(setSubscription);
        subscriptionSettingsStore!
          .getInvoicesList(organizationId)
          .then(setInvoices);
      }
    }, []);

    const renew = () => {
      subscriptionSettingsStore!
        .renewSubscription(organizationId!)
        .then(link => {
          window.open(link, '_self');
        });
    };
    const create = (id: number) => {
      subscriptionSettingsStore!
        .buySubscription(organizationId!, id)
        .then(link => {
          window.open(link, '_self');
        });
    };
    const update = (id: number) => {
      const plan = getSubscriptionPlan(id);
      const isAnnual = isAnnualSubscription(id);
      if (!plan) throw new Error('Plan not found');
      const openingPayload: ConfirmPaymentPayload = {
        uiType: 'planChange',
        planName: plan.title,
        price: isAnnual
          ? plan.priceAnnual + '/year'
          : plan.priceMonthly + '/mo',
        nextBillingDate: subscription
          ? formatDateTimeTextFormat(subscription.currentPeriodEndDate)
          : '',
        keyFeatures: isAnnual ? plan.featuresAnnual : plan.featuresMonthly,
      };

      subscriptionSettingsStore!
        .checkIfUpdateRequiresConfirmation(organizationId!, id)
        .then(needToConfirm => {
          if (needToConfirm) {
            modalProviderStore!.openModal(
              CONFIRM_PAYMENT_MODAL,
              (accepted: boolean) => {
                if (accepted) {
                  upgradeTier();
                }
              },
              openingPayload,
            );
          } else {
            upgradeTier();
          }
        });

      function upgradeTier() {
        subscriptionSettingsStore!
          .upgradeTier(organizationId!, id)
          .then(res => {
            if (res?.sessionLink) {
              window.open(res.sessionLink, '_self');
            } else if (res?.success) {
              navigate(`/subscription/${organizationId!}/success_update`);
            }
          });
      }
    };

    const onSelectSubscription = (newSubscriptionId?: number) => {
      if (newSubscriptionId && organizationId) {
        if (newSubscriptionId === SUBSCRIPTION_ENTERPRISE) {
          window.open('https://tapybl.com/request-quote/');
        } else if (subscription?.organizationSubscriptionStatus !== 0) {
          // status 0 is active
          if (
            subscription?.type === newSubscriptionId &&
            subscription?.type !== SUBSCRIPTION_TRIAL
          ) {
            renew();
          } else {
            create(newSubscriptionId);
          }
        } else if (subscription?.type === SUBSCRIPTION_TRIAL) {
          create(newSubscriptionId);
        } else {
          update(newSubscriptionId);
        }
      } else {
        window.open('https://tapybl.com/request-quote/', '_blank');
      }
    };

    const onChangePaymentMethod = () => {
      if (organizationId) {
        subscriptionSettingsStore!
          .getPaymentMethodSession(organizationId)
          .then(res => {
            if (res) {
              const win = window.open(res, '_blank');
              if (win != null) {
                win.focus();
              }
            }
          });
      }
    };

    const onCancelSubscription = () => {
      if (organizationId) {
        subscriptionSettingsStore
          ?.cancelSubscription(organizationId)
          .then(success => {
            if (success) {
              window.location.reload();
            }
          });
      }
    };

    return subscription ? (
      <>
        <SubscriptionSettingsUI
          onSelectSubscription={onSelectSubscription}
          subscription={subscription}
          onCancelSubscription={() => setShowConfirmCancel(true)}
          onChangePaymentMethod={onChangePaymentMethod}
          invoices={invoices}
          isLoading={subscriptionSettingsStore!.isLoading}
          onShowFullList={() => setShowFeatures(true)}
        />
        <ConfirmationModal
          title={settingsText.areYouSureCancelSubscription}
          onAccept={onCancelSubscription}
          onCancel={() => setShowConfirmCancel(false)}
          shown={showConfirmCancel}
        />
        {showFeatures && (
          <FeaturesTableModal onClose={() => setShowFeatures(false)} />
        )}
      </>
    ) : null;
  }),
);

export default SubscriptionSettingsContainer;
